import * as THREE from 'three';
import { TAU, smoothstep, lerp, rr, rnd, clamp } from './util.js';
import { GeoBuilder } from './geo.js';

/**
 * Sky dome + sun/moon + clouds + the whole time-of-day lighting rig
 * (directional light w/ shadows, hemisphere fill, fog, exposure).
 */

const KEYS = [
  // h, zenith, horizon, sun colour, sunI, hemi sky, hemi ground, hemiI, exposure, night(0..1)
  { h: 0.0, zen: '#070c2a', hor: '#1a2757', sun: '#8aa0ff', sunI: 0, hs: '#3a4d9a', hg: '#1a2140', hi: 1.15, exp: 1.18, night: 1 },
  { h: 4.6, zen: '#0d1538', hor: '#2a3468', sun: '#8aa0ff', sunI: 0, hs: '#3a4d9a', hg: '#1a2140', hi: 1.15, exp: 1.18, night: 1 },
  { h: 5.6, zen: '#26356f', hor: '#e58a8a', sun: '#ff9a70', sunI: 0.5, hs: '#7f86d0', hg: '#4b3a4e', hi: 0.9, exp: 1.1, night: 0.35 },
  { h: 6.5, zen: '#5b86d6', hor: '#ffbf95', sun: '#ffb27a', sunI: 2.3, hs: '#b8c4f4', hg: '#8c7658', hi: 1.35, exp: 1.02, night: 0 },
  { h: 8.5, zen: '#4b98ea', hor: '#c4e6fa', sun: '#fff0d4', sunI: 3.0, hs: '#b9dcff', hg: '#86b85a', hi: 1.15, exp: 1.0, night: 0 },
  { h: 12, zen: '#3b8ae6', hor: '#bfe4f8', sun: '#fffaf0', sunI: 3.3, hs: '#bfe0ff', hg: '#88c35c', hi: 1.2, exp: 0.98, night: 0 },
  { h: 15.5, zen: '#4d8fe2', hor: '#d2e6f2', sun: '#fff0d0', sunI: 3.1, hs: '#c8deff', hg: '#94b95c', hi: 1.25, exp: 1.02, night: 0 },
  { h: 17.0, zen: '#5b86dc', hor: '#ffd9a2', sun: '#ffc98a', sunI: 3.3, hs: '#dfd3f0', hg: '#b09a58', hi: 1.6, exp: 1.1, night: 0 },
  { h: 18.0, zen: '#4b5fb8', hor: '#ff8f5c', sun: '#ff9a58', sunI: 2.8, hs: '#dcc4ee', hg: '#b08060', hi: 1.85, exp: 1.24, night: 0 },
  { h: 18.8, zen: '#2b2f78', hor: '#d9607e', sun: '#e0506a', sunI: 1.7, hs: '#9a86d0', hg: '#5a4060', hi: 1.55, exp: 1.32, night: 0.25 },
  { h: 20.0, zen: '#141c50', hor: '#4a4a92', sun: '#8aa0ff', sunI: 0, hs: '#4a5aa8', hg: '#242a4c', hi: 1.35, exp: 1.32, night: 0.8 },
  { h: 21.5, zen: '#070c2a', hor: '#1a2757', sun: '#8aa0ff', sunI: 0, hs: '#3a4d9a', hg: '#1a2140', hi: 1.15, exp: 1.18, night: 1 },
  { h: 24.0, zen: '#070c2a', hor: '#1a2757', sun: '#8aa0ff', sunI: 0, hs: '#3a4d9a', hg: '#1a2140', hi: 1.15, exp: 1.18, night: 1 },
].map((k) => ({
  ...k,
  zen: new THREE.Color(k.zen), hor: new THREE.Color(k.hor), sun: new THREE.Color(k.sun),
  hs: new THREE.Color(k.hs), hg: new THREE.Color(k.hg),
}));

const MOON_COLOR = new THREE.Color('#a9bbff');

const skyVert = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = p.xyww; // always at far plane
  }
`;

const skyFrag = /* glsl */ `
  uniform vec3 uZenith, uHorizon, uSunDir, uMoonDir, uSunColor;
  uniform float uNight, uTime, uSunUp;
  varying vec3 vDir;

  float hash13(vec3 p) {
    p = fract(p * 0.1031);
    p += dot(p, p.zyx + 31.32);
    return fract((p.x + p.y) * p.z);
  }

  void main() {
    vec3 d = normalize(vDir);
    float y = d.y;
    // gradient
    float tz = pow(clamp(y, 0.0, 1.0), 0.55);
    vec3 col = mix(uHorizon, uZenith, tz);
    // below the horizon just fade to haze
    col = mix(col, uHorizon * 0.9, smoothstep(0.0, -0.25, y));

    // horizon glow toward the sun
    float sd = max(dot(d, uSunDir), 0.0);
    vec2 hd = normalize(d.xz + 1e-5), hs = normalize(uSunDir.xz + 1e-5);
    float az = pow(max(dot(hd, hs), 0.0), 3.0);
    float band = exp(-abs(y) * 5.5);
    col += uSunColor * az * band * 0.55 * uSunUp;
    col += uSunColor * pow(sd, 6.0) * 0.28 * uSunUp;
    col += uSunColor * pow(sd, 80.0) * 0.6 * uSunUp;
    // sun disc (HDR so it blooms)
    float disc = smoothstep(0.99935, 0.99965, sd);
    col = mix(col, uSunColor * 6.0, disc * uSunUp * step(0.0, y + 0.02));

    // stars
    float star = 0.0;
    if (uNight > 0.01 && y > -0.02) {
      vec3 p = d * 110.0;
      vec3 id = floor(p);
      float h = hash13(id);
      vec3 f = fract(p) - 0.5;
      float sz = 0.10 + 0.22 * hash13(id + 7.0);
      float tw = 0.6 + 0.4 * sin(uTime * (1.5 + h * 3.0) + h * 60.0);
      star = step(0.972, h) * smoothstep(sz, 0.0, length(f)) * tw;
      star *= smoothstep(0.0, 0.25, y);
    }
    col += vec3(1.0, 0.96, 0.9) * star * 2.2 * uNight;

    // moon
    float md = dot(d, uMoonDir);
    float moon = smoothstep(0.9982, 0.9990, md);
    vec3 moonCol = vec3(0.95, 0.96, 1.0) * (0.85 + 0.15 * hash13(floor(d * 60.0)));
    col = mix(col, moonCol * 2.2, moon * uNight);
    col += vec3(0.5, 0.6, 1.0) * pow(max(md, 0.0), 40.0) * 0.35 * uNight;

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class Sky {
  constructor(scene, renderer, shadowRes = 4096) {
    this.scene = scene;
    this.renderer = renderer;
    this.hour = 15.2;
    this.fogScale = 1;
    this.night = 0; // 0 day .. 1 full night (drives windows, lamps, fireflies)
    this.dayW = 1;
    this.sunDir = new THREE.Vector3(0, 1, 0);
    this.moonDir = new THREE.Vector3(0, -1, 0);
    this.horizon = new THREE.Color();
    this.zenith = new THREE.Color();
    this.sunColor = new THREE.Color();

    // ---- dome
    this.uniforms = {
      uZenith: { value: new THREE.Color() },
      uHorizon: { value: new THREE.Color() },
      uSunDir: { value: new THREE.Vector3() },
      uMoonDir: { value: new THREE.Vector3() },
      uSunColor: { value: new THREE.Color() },
      uNight: { value: 0 },
      uSunUp: { value: 1 },
      uTime: { value: 0 },
    };
    this.dome = new THREE.Mesh(
      new THREE.SphereGeometry(900, 48, 24),
      new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: skyVert,
        fragmentShader: skyFrag,
        side: THREE.BackSide,
        depthWrite: false,
        depthTest: false,
        fog: false,
      }),
    );
    this.dome.frustumCulled = false;
    this.dome.renderOrder = -1000;
    scene.add(this.dome);

    // ---- lights
    this.sunLight = new THREE.DirectionalLight('#ffffff', 3);
    this.sunLight.castShadow = true;
    const sh = this.sunLight.shadow;
    sh.mapSize.set(shadowRes, shadowRes);
    this.shadowSize = 46;
    Object.assign(sh.camera, { left: -this.shadowSize, right: this.shadowSize, top: this.shadowSize, bottom: -this.shadowSize, near: 1, far: 400 });
    sh.bias = -0.0004;
    sh.normalBias = 0.06;
    sh.radius = 2.5;
    scene.add(this.sunLight, this.sunLight.target);

    this.hemi = new THREE.HemisphereLight('#bfe0ff', '#88c35c', 1.1);
    scene.add(this.hemi);

    scene.fog = new THREE.FogExp2('#c9e6f6', 0.0033);

    // ---- clouds
    this.clouds = this._makeClouds();
    scene.add(this.clouds);

    this._focus = new THREE.Vector3();
    this._tmpA = new THREE.Vector3();
    this._tmpB = new THREE.Vector3();
    this._c = new THREE.Color();
  }

  _makeClouds() {
    const group = new THREE.Group();
    for (let i = 0; i < 16; i++) {
      const b = new GeoBuilder();
      const blobs = 4 + Math.floor(rnd() * 4);
      const len = rr(10, 20);
      for (let k = 0; k < blobs; k++) {
        const t = blobs === 1 ? 0 : k / (blobs - 1) - 0.5;
        const r = rr(4, 7) * (1 - Math.abs(t) * 0.9);
        b.ico(r, 1, t * len, rr(-0.5, 1.2), rr(-2, 2), '#ffffff', { sy: 0.62, jitter: 0.06 });
      }
      const mesh = new THREE.Mesh(
        b.build(),
        new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, flatShading: true, emissive: '#ffffff', emissiveIntensity: 1 }),
      );
      const ang = rnd() * TAU, dist = rr(40, 240);
      mesh.position.set(Math.cos(ang) * dist, rr(58, 95), Math.sin(ang) * dist);
      mesh.rotation.y = rnd() * TAU;
      mesh.scale.setScalar(rr(0.9, 1.7));
      mesh.userData.speed = rr(1.2, 2.4);
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      group.add(mesh);
    }
    return group;
  }

  /** Sample keyframes at an hour. */
  _sample(hour) {
    let a = KEYS[0], b = KEYS[1];
    for (let i = 0; i < KEYS.length - 1; i++) {
      if (hour >= KEYS[i].h && hour <= KEYS[i + 1].h) { a = KEYS[i]; b = KEYS[i + 1]; break; }
    }
    const t = (hour - a.h) / (b.h - a.h || 1);
    return { a, b, t };
  }

  setHour(h) {
    this.hour = ((h % 24) + 24) % 24;
  }

  /**
   * @param focus world point the shadow map should cover (camera target)
   */
  update(dt, time, camera, focus) {
    const hour = this.hour;
    const { a, b, t } = this._sample(hour);
    const lerpC = (out, ka, kb) => out.copy(ka).lerp(kb, t);

    // ----- sun / moon direction
    const ang = ((hour - 6) / 24) * TAU;
    this.sunDir.set(Math.cos(ang), Math.sin(ang), 0.32).normalize();
    this.moonDir.copy(this.sunDir).multiplyScalar(-1);

    const sunUp = smoothstep(-0.04, 0.1, this.sunDir.y);
    this.dayW = sunUp;
    this.night = lerp(a.night, b.night, t);

    // ----- sky
    lerpC(this.zenith, a.zen, b.zen);
    lerpC(this.horizon, a.hor, b.hor);
    lerpC(this.sunColor, a.sun, b.sun);
    const u = this.uniforms;
    u.uZenith.value.copy(this.zenith);
    u.uHorizon.value.copy(this.horizon);
    u.uSunColor.value.copy(this.sunColor);
    u.uSunDir.value.copy(this.sunDir);
    u.uMoonDir.value.copy(this.moonDir);
    u.uNight.value = this.night;
    u.uSunUp.value = smoothstep(-0.06, 0.02, this.sunDir.y);
    u.uTime.value = time;
    this.dome.position.copy(camera.position);

    // ----- fog follows the horizon colour so hills melt into the sky
    this.scene.fog.color.copy(this.horizon);
    this.scene.fog.density = lerp(0.0033, 0.0046, this.night * 0.6) * this.fogScale;

    // ----- hemisphere
    lerpC(this.hemi.color, a.hs, b.hs);
    lerpC(this.hemi.groundColor, a.hg, b.hg);
    this.hemi.intensity = lerp(a.hi, b.hi, t);

    // ----- directional light: sun by day, moon by night
    const sunI = lerp(a.sunI, b.sunI, t) * sunUp;
    const moonI = 0.85 * (1 - sunUp) * smoothstep(-0.05, 0.12, this.moonDir.y);
    const useSun = sunI >= moonI;
    const L = this.sunLight;
    if (useSun) {
      L.color.copy(this.sunColor);
      L.intensity = sunI;
      this._tmpA.copy(this.sunDir);
    } else {
      L.color.copy(MOON_COLOR);
      L.intensity = moonI;
      this._tmpA.copy(this.moonDir);
    }
    // keep light direction above horizon a little so shadows do not go sideways to infinity
    this._tmpA.y = Math.max(this._tmpA.y, useSun ? 0.34 : 0.2);
    this._tmpA.normalize();

    // snap shadow frustum to texels to avoid shimmering
    const size = this.shadowSize * 2;
    const texel = size / L.shadow.mapSize.x;
    const dir = this._tmpA;
    const right = this._tmpB.set(0, 1, 0).cross(dir).normalize();
    const up = new THREE.Vector3().crossVectors(dir, right).normalize();
    const f = this._focus.copy(focus);
    let x = f.dot(right), y = f.dot(up), z = f.dot(dir);
    x = Math.round(x / texel) * texel;
    y = Math.round(y / texel) * texel;
    f.set(0, 0, 0).addScaledVector(right, x).addScaledVector(up, y).addScaledVector(dir, z);
    L.target.position.copy(f);
    L.position.copy(f).addScaledVector(dir, 160);
    L.target.updateMatrixWorld();

    // exposure
    this.renderer.toneMappingExposure = lerp(a.exp, b.exp, t);

    // ----- clouds drift + take on light colour
    for (const c of this.clouds.children) {
      c.position.x += c.userData.speed * dt;
      if (c.position.x > 260) c.position.x = -260;
    }
    const cloudDay = this._c.set('#ffffff').lerp(this.horizon, 0.28 * (1 - this.night)).lerp(new THREE.Color('#5a6aa8'), this.night * 0.8);
    for (const c of this.clouds.children) {
      c.material.color.copy(cloudDay);
      c.material.emissive.copy(this.horizon).multiplyScalar(0.25 * (1 - this.night));
    }
  }
}
